import { useState, useEffect, useCallback } from 'react';
import { partnerService } from '../services/partnerService';
import { DashboardOverview, AuditLog } from '../types/partner';
import { useAuth } from '../context/AuthContext';
import { config } from '../config/environment';

// Mock dashboard data for local development (dev mode only)
const getMockDashboardOverview = (): DashboardOverview => ({
  totalCustomers: 3,
  activeCustomers: 3,
  totalDevices: 127,
  activeDevices: 98,
  totalUsers: 175,
  activeUsers: 142,
  recentActivity: [],
});

const getMockRecentActivity = (): AuditLog[] => {
  const now = new Date();
  const activities: AuditLog[] = [
    {
      id: 'audit-1',
      timestamp: new Date(now.getTime() - 5 * 60 * 1000).toISOString(), // 5 min ago
      userId: 'user-123',
      customerId: 'mock-customer-1',
      partnerId: 'mock-partner-1',
      eventType: 'Device Registration',
      eventStatus: 'Success',
      resourceType: 'Device',
      resourceId: 'device-456',
      details: 'New device registered: Laptop-DEV-001',
      ipAddress: '192.168.1.100',
    },
    {
      id: 'audit-2',
      timestamp: new Date(now.getTime() - 15 * 60 * 1000).toISOString(), // 15 min ago
      userId: 'user-456',
      customerId: 'mock-customer-2',
      partnerId: 'mock-partner-1',
      eventType: 'User Created',
      eventStatus: 'Success',
      resourceType: 'User',
      resourceId: 'user-789',
      details: 'New user account created: john.doe@techstart.example.com',
      ipAddress: '10.0.0.50',
    },
    {
      id: 'audit-3',
      timestamp: new Date(now.getTime() - 32 * 60 * 1000).toISOString(), // 32 min ago
      userId: 'user-123',
      customerId: 'mock-customer-1',
      partnerId: 'mock-partner-1',
      eventType: 'License Updated',
      eventStatus: 'Success',
      resourceType: 'License',
      resourceId: 'license-abc',
      details: 'License count updated from 45 to 50',
      ipAddress: '192.168.1.100',
    },
    {
      id: 'audit-4',
      timestamp: new Date(now.getTime() - 45 * 60 * 1000).toISOString(), // 45 min ago
      userId: 'user-789',
      customerId: 'mock-customer-3',
      partnerId: 'mock-partner-1',
      eventType: 'Device Sync',
      eventStatus: 'Success',
      resourceType: 'Device',
      resourceId: 'device-789',
      details: 'Device sync completed successfully',
      ipAddress: '172.16.0.25',
    },
    {
      id: 'audit-5',
      timestamp: new Date(now.getTime() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
      userId: 'user-456',
      customerId: 'mock-customer-2',
      partnerId: 'mock-partner-1',
      eventType: 'Configuration Change',
      eventStatus: 'Success',
      resourceType: 'Settings',
      resourceId: 'settings-123',
      details: 'Security policy updated',
      ipAddress: '10.0.0.50',
    },
    {
      id: 'audit-6',
      timestamp: new Date(now.getTime() - 1.5 * 60 * 60 * 1000).toISOString(), // 1.5 hours ago
      userId: 'user-123',
      customerId: 'mock-customer-1',
      partnerId: 'mock-partner-1',
      eventType: 'Report Generated',
      eventStatus: 'Success',
      resourceType: 'Report',
      resourceId: 'report-xyz',
      details: 'Monthly usage report generated',
      ipAddress: '192.168.1.100',
    },
    {
      id: 'audit-7',
      timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
      userId: 'user-789',
      customerId: 'mock-customer-3',
      partnerId: 'mock-partner-1',
      eventType: 'User Deactivated',
      eventStatus: 'Success',
      resourceType: 'User',
      resourceId: 'user-999',
      details: 'User account deactivated: former.employee@global.example.com',
      ipAddress: '172.16.0.25',
    },
    {
      id: 'audit-8',
      timestamp: new Date(now.getTime() - 2.5 * 60 * 60 * 1000).toISOString(), // 2.5 hours ago
      userId: 'user-456',
      customerId: 'mock-customer-2',
      partnerId: 'mock-partner-1',
      eventType: 'API Error',
      eventStatus: 'Error',
      resourceType: 'API',
      resourceId: 'api-call-123',
      details: 'Failed to sync device data: Connection timeout',
      ipAddress: '10.0.0.50',
    },
    {
      id: 'audit-9',
      timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
      userId: 'user-123',
      customerId: 'mock-customer-1',
      partnerId: 'mock-partner-1',
      eventType: 'Backup Completed',
      eventStatus: 'Success',
      resourceType: 'Backup',
      resourceId: 'backup-456',
      details: 'Daily backup completed successfully',
      ipAddress: '192.168.1.100',
    },
    {
      id: 'audit-10',
      timestamp: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
      userId: 'user-789',
      customerId: 'mock-customer-3',
      partnerId: 'mock-partner-1',
      eventType: 'Security Alert',
      eventStatus: 'Pending',
      resourceType: 'Security',
      resourceId: 'alert-789',
      details: 'Unusual login activity detected',
      ipAddress: '172.16.0.25',
    },
  ];
  return activities;
};

interface UseDashboardReturn {
  overview: DashboardOverview | null;
  recentActivity: AuditLog[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  clearError: () => void;
}

// Type guard to check if an object is a valid DashboardOverview
function isDashboardOverview(obj: any): obj is DashboardOverview {
  return obj && 
    typeof obj === 'object' &&
    typeof obj.totalCustomers === 'number' &&
    typeof obj.activeCustomers === 'number' &&
    typeof obj.totalDevices === 'number' &&
    typeof obj.activeDevices === 'number' &&
    typeof obj.totalUsers === 'number' &&
    typeof obj.activeUsers === 'number';
}

export function useDashboard(): UseDashboardReturn {
  const { partner, isAuthenticated, hasPartnerAccess } = useAuth();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);



  // Fetch dashboard data - Live data only (or mock data in dev mode)
  const fetchDashboardData = useCallback(async () => {
    if (!partner?.partnerId || !isAuthenticated || !hasPartnerAccess) {
      return;
    }

    // DEV MODE: Return mock data instead of making API calls
    if (config.features.devMode) {
      setIsLoading(true);
      // Simulate a brief loading delay for realism
      await new Promise(resolve => setTimeout(resolve, 300));
      setOverview(getMockDashboardOverview());
      setRecentActivity(getMockRecentActivity());
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Backend API calls for live data
      const overviewResponse = await partnerService.getDashboardOverview(partner.partnerId);
      const activityResponse = await partnerService.getRecentActivity(partner.partnerId, 10);
      
      // Check if overviewResponse has the expected structure or is the data directly
      if (overviewResponse) {
        // If it's wrapped in an API response structure
        if ('success' in overviewResponse && 'data' in overviewResponse) {
          if (overviewResponse.success && overviewResponse.data && isDashboardOverview(overviewResponse.data)) {
            setOverview(overviewResponse.data);
          } else {
            setError(overviewResponse.error || 'Failed to load dashboard overview');
          }
        } 
        // If it's direct data (use type guard)
        else if (isDashboardOverview(overviewResponse)) {
          setOverview(overviewResponse);
        } 
        else {
          setError('Invalid dashboard overview data received');
        }
      } else {
        setError('No dashboard overview data received');
      }
      
      // Check if activityResponse has the expected structure or is the data directly
      if (activityResponse) {
        // If it's wrapped in an API response structure
        if ('success' in activityResponse && 'data' in activityResponse) {
          if (activityResponse.success && Array.isArray(activityResponse.data)) {
            setRecentActivity(activityResponse.data);
          } else {
            setRecentActivity([]);
          }
        }
        // If it's direct data (should be an array)
        else if (Array.isArray(activityResponse)) {
          setRecentActivity(activityResponse);
        }
        else {
          setRecentActivity([]);
        }
      } else {
        setRecentActivity([]);
      }
        
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [partner, isAuthenticated, hasPartnerAccess]);

  // Load data on mount and when partner changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Refresh data function
  const refreshData = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    overview,
    recentActivity,
    isLoading,
    error,
    refreshData,
    clearError
  };
} 